import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button, {
  ButtonTypes,
} from "../../../../common/components/button/button";
import { useLoadHandler } from "../../../../common/customHooks/useLoadHandler";
import { LoadingStatus } from "../../../../common/enums/loadingStatus";
import { QuestionTypes } from "../../../../common/enums/questionTypes";
import {
  clearCreateCampaignData,
  loadCreateCampaignData,
  setCampaignBlock,
  setCampaignDescription,
  setCampaignTitle,
  setCoverDetail,
  setCustomLink,
  setTheme,
  setTicketDetail,
} from "../../../../common/reducers/createCampaignReducer";
import { CampaignApi } from "../../../../common/services/api/campaignApi";
import store, {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../../store";
import CreateCampaignCover from "../../components/createCampaignCover/createCampaignCover";
import CreateCampaignCoverSkeleton from "../../components/createCampaignCoverSkeleton/createCampaignCoverSkeleton";
import CreateCampaignQuestion from "../../components/createCampaignQuestion/createCampaignQuestion";
import CreateCampaignTicket from "../../components/createCampaignTicket/createCampaignTicket";
import CreateCampaignLayout from "../../components/layouts/createCampaignLayout/createCampaignLayout";
import {
  CoverDetailModel,
  CreateCampaignModel,
} from "../../models/createCampaignModel";
import { CustomLinkModel } from "../../models/customLinkModel";
import { CampaignBlockModel } from "../../models/questionModel";
import { ThemeModel } from "../../models/themeModel";
import { TicketDetailModel } from "../../models/ticketDetailModel";
import "./createCampaignPage.scss";

export default function CreateCampaignPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [index, setIndex] = useState(
    Number(new URLSearchParams(window.location.search).get("index")) ?? 0
  );
  const createCampaignData = useAppSelector(
    (state: RootState) => state.createCampaign
  );

  function setCoverDetailData(coverDetailData: CoverDetailModel) {
    dispatch(setCoverDetail(coverDetailData));
  }

  function setCampaignBlockList(campaignBlockList: CampaignBlockModel[]) {
    dispatch(setCampaignBlock(campaignBlockList));
  }

  function setCampaignTicket(TicketDetail: TicketDetailModel) {
    dispatch(setTicketDetail(TicketDetail));
  }

  function setThemeData(themeData: ThemeModel) {
    dispatch(setTheme(themeData));
  }
  function setCustomLinkData(customLinkData: CustomLinkModel) {
    dispatch(setCustomLink(customLinkData));
  }

  function setTitle(title: string) {
    dispatch(setCampaignTitle(title));
  }

  function setDescription(title: string) {
    dispatch(setCampaignDescription(title));
  }

  const {
    loadingStatus: dataLoadingStatus,
    callFetchAsync: dataCallFetchAsync,
  } = useLoadHandler(async (id: string) => {
    let result = await CampaignApi().campaignDetail(id);
    return () => dispatch(loadCreateCampaignData(result));
  });

  const { loadingStatus, callFetchAsync } = useLoadHandler(
    async (data: CreateCampaignModel) => {
      let result = await CampaignApi().createCampaign(data);
      let url = new URLSearchParams(window.location.search);
      url.set("id", result.id);
      navigate("?" + url.toString());
      dispatch(loadCreateCampaignData(result));
    }
  );

  async function navigateBack() {
    let url = new URLSearchParams(window.location.search);
    if (url.get("index"))
      url.set("index", `${Number.parseInt(url.get("index")!) - 1}`);
    navigate("?" + url.toString());
  }

  async function navigateNext() {
    let url = new URLSearchParams(window.location.search);
    if (url.get("index"))
      url.set("index", `${Number.parseInt(url.get("index")!) + 1}`);
    navigate("?" + url.toString());
  }

  function getTotalScore() {
    let score = 0;
    createCampaignData.campaignBlock.map((e) => {
      if (
        e.questionBlockDetail?.questionType == QuestionTypes.MULTIPLE_CHOICE
      ) {
        score +=
          [...(e.questionBlockDetail?.options ?? [])].sort(
            (a, b) => (b.score ?? 0) - (a.score ?? 0)
          )?.[0]?.score ?? 0;
      } else {
        e.questionBlockDetail?.options?.map((e2) => (score += e2.score ?? 0));
      }
    });
    return score;
  }

  useEffect(() => {
    let id = new URLSearchParams(window.location.search).get("id");
    if (id) dataCallFetchAsync(id);
  }, []);

  return (
    <Provider store={store}>
      <CreateCampaignLayout
        onClickSave={() => callFetchAsync(createCampaignData)}
        saveButtonLoadingStatus={loadingStatus}
        campaignDescription={createCampaignData.campaignDescription}
        campaignTitle={createCampaignData.campaignTitle}
        customLink={createCampaignData.customLink}
        theme={createCampaignData.theme}
        setTitle={setTitle}
        setCustomLinkData={setCustomLinkData}
        setDescription={setDescription}
        setThemeData={setThemeData}
      >
        <div
          className="back-button"
          onClick={() => {
            if (index == 0) {
              dispatch(clearCreateCampaignData());
              navigate("/home");
            } else {
              navigateBack();
              setIndex(index - 1);
            }
          }}
        >
          {"< Back"}
        </div>
        {dataLoadingStatus != LoadingStatus.LOADING ? (
          <>
            {index == 0 && (
              <CreateCampaignCover
                themeData={createCampaignData.theme}
                initialValue={createCampaignData.coverDetail}
                onChange={(e) => setCoverDetailData(e)}
              />
            )}
            {index == 1 && (
              <CreateCampaignQuestion
                initialValue={createCampaignData.campaignBlock}
                onChange={(e) => setCampaignBlockList(e)}
              />
            )}
            {index == 2 && (
              <CreateCampaignTicket
                totalScore={getTotalScore()}
                initialValue={JSON.parse(
                  JSON.stringify(createCampaignData.TicketDetail)
                )}
                onChange={(e) =>
                  setCampaignTicket(JSON.parse(JSON.stringify(e)))
                }
              />
            )}
            <div style={{ flex: 1 }} />
            <div style={{ alignSelf: "flex-end" }}>
              <Button
                type={ButtonTypes.PRIMARY}
                loadingStatus={loadingStatus}
                text={"Continue"}
                onClick={async () => {
                  if (index == 2) {
                    await callFetchAsync(createCampaignData);
                    navigate("/home");
                  } else {
                    navigateNext();
                    setIndex(index + 1);
                  }
                }}
              />
            </div>
          </>
        ) : (
          <CreateCampaignCoverSkeleton />
        )}
      </CreateCampaignLayout>
    </Provider>
  );
}
